import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, FormGroup, Label, Row } from 'reactstrap';
import {
  ColumnType,
  UtrValueType,
  frameworks as FRAMEWORKS_REGISTRY,
  standards as STANDARDS_REGISTRY,
} from '@g17eco/core';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { getFilterOption } from '@g17eco/molecules/select/utils';

// Hoisted: getFilterOption() returns a fresh closure each call, which
// would defeat react-select's memoisation if invoked inline per render.
const SELECT_FILTER_OPTION = getFilterOption<Option<string> | null>();
import { useGetInitiativeBlueprintQuestionsQuery } from '@api/admin-dashboard';
import { useGetMetricGroupsDropdownQuery } from '@api/initiatives';
import type { UsedScopes } from '@api/organization';
import { getUsedStandardAndFrameworkOptions } from '../../../../../constants/standards-frameworks';
import { getCustomGroupOptions, isMatchSelectedPacks } from '@utils/metricGroup';
import { Form, OnChangeForm } from './types';
import { NormalisedOverrideConfig } from '@g17eco/types/custom-report';
import { TableColumn, UniversalTrackerBlueprintMin } from '@g17eco/types/universalTracker';
import { SURVEY } from '@constants/terminology';
import { SurveyScope } from '@g17eco/types/surveyCommon';

interface Props {
  initiativeId: string;
  form: Form;
  // Limits overridable-UTR detection to metrics actually in the report
  // (merged scope of selected surveys), not the full initiative blueprint.
  reportScope: SurveyScope;
  onChangeForm: OnChangeForm;
}

interface ExtraEntry {
  id: string;
  label: string;
  columnCode: string;
}

interface OverrideEntry {
  id: string;
  utrCode: string;
  // moduleId is UI-only and not persisted to NormalisedOverrideConfig.
  moduleId?: string;
  activityColumn?: string;
  extras: ExtraEntry[];
}

const makeId = () => crypto.randomUUID();

const toEntries = (
  overrides: Record<string, NormalisedOverrideConfig> | undefined
): OverrideEntry[] =>
  Object.entries(overrides ?? {}).map(([utrCode, config]) => ({
    id: makeId(),
    utrCode,
    activityColumn: config.activityColumn,
    extras: (config.extras ?? []).map((extra) => ({ id: makeId(), ...extra })),
  }));

const toRecord = (entries: OverrideEntry[]): Record<string, NormalisedOverrideConfig> => {
  const result: Record<string, NormalisedOverrideConfig> = {};
  for (const entry of entries) {
    if (entry.utrCode.trim() === '') continue;
    result[entry.utrCode] = {
      activityColumn: entry.activityColumn,
      extras: entry.extras
        .filter((e) => e.label.trim() !== '' && e.columnCode.trim() !== '')
        .map(({ label, columnCode }) => ({ label, columnCode })),
    };
  }
  return result;
};

const countIncompleteRows = (entries: OverrideEntry[]) => {
  let drops = 0;
  for (const entry of entries) {
    if (entry.utrCode.trim() === '') {
      drops += 1;
      continue;
    }
    drops += entry.extras.filter(
      (e) => e.label.trim() === '' || e.columnCode.trim() === ''
    ).length;
  }
  return drops;
};

const isNumericColumnType = (type: ColumnType) =>
  type === ColumnType.Number || type === ColumnType.Percentage;

const isNumericCol = (col: TableColumn) => isNumericColumnType(col.type);

// Single-select only — ValueListMulti's array values can't map to one Activity dimension.
// Legacy text+listId columns predate the dedicated ValueList type but still qualify.
const isValueListLike = (col: TableColumn): boolean =>
  col.type === ColumnType.ValueList || (col.type === ColumnType.Text && Boolean(col.listId));

// Mirrors the API `RequiresActivityConfig` shape: 2+ ValueList + exactly 1
// Numeric + no other column types. Tables outside this shape are either
// handled automatically by the backend (override would be a no-op) or
// unconditionally dropped (override can't recover them) — showing them here
// would mislead the user either way.
const isOverridableShapeUtr = (utr: UniversalTrackerBlueprintMin) => {
  if (utr.valueType !== UtrValueType.Table) return false;
  const columns = utr.valueValidation?.table?.columns ?? [];
  if (columns.length === 0) return false;
  const valueListCount = columns.filter(isValueListLike).length;
  const numericCount = columns.filter(isNumericCol).length;
  return (
    valueListCount >= 2 &&
    numericCount === 1 &&
    valueListCount + numericCount === columns.length
  );
};

const buildColumnOptions = (
  utr: UniversalTrackerBlueprintMin | undefined,
  filter?: (col: TableColumn) => boolean
): Option<string>[] => {
  const columns = utr?.valueValidation?.table?.columns ?? [];
  return columns
    .filter((col) => (filter ? filter(col) : true))
    .map((col) => ({
      value: col.code,
      label: col.name,
      searchString: `${col.code} ${col.name}`,
      tooltip: col.code,
    }));
};

export const NormalisedOverridesEditor = ({ initiativeId, form, reportScope, onChangeForm }: Props) => {
  const {
    data: blueprintUtrs = [],
    isLoading: blueprintLoading,
    isError: blueprintError,
  } = useGetInitiativeBlueprintQuestionsQuery({ initiativeId }, { skip: !initiativeId });
  const {
    data: metricGroups = [],
    isError: metricGroupsError,
  } = useGetMetricGroupsDropdownQuery(initiativeId, { skip: !initiativeId });

  // Derive used standards/frameworks from the loaded blueprint rather than calling
  // /scopes/used — the blueprint is already present and is authoritative for this view.
  const usedPacks = useMemo<UsedScopes>(() => {
    const standards: Record<string, string[]> = {};
    const frameworks: Record<string, string[]> = {};
    for (const utr of blueprintUtrs) {
      if (utr.type && utr.type in STANDARDS_REGISTRY) {
        standards[utr.type] = [];
      }
      if (utr.alternatives) {
        for (const code of Object.keys(utr.alternatives)) {
          if (code in STANDARDS_REGISTRY) {
            standards[code] = [];
          }
        }
      }
      if (utr.tags) {
        for (const code of Object.keys(utr.tags)) {
          if (code in FRAMEWORKS_REGISTRY) {
            frameworks[code] = [];
          }
        }
      }
    }
    return { standards, frameworks };
  }, [blueprintUtrs]);

  const utrByCode = useMemo(
    () => new Map(blueprintUtrs.map((u) => [u.code, u])),
    [blueprintUtrs]
  );

  const moduleOptions = useMemo<Option<string>[]>(
    () => [...getCustomGroupOptions(metricGroups), ...getUsedStandardAndFrameworkOptions(usedPacks)],
    [metricGroups, usedPacks]
  );

  // Packs in scope across the report's selected surveys: standard codes,
  // framework codes, and custom-metric-group ids (the user's primary use
  // case). Empty when no subsidiaries/dates are picked yet — show 0 in
  // that case rather than the entire blueprint.
  const scopePacks = useMemo(
    () => [...reportScope.standards, ...reportScope.frameworks, ...reportScope.custom],
    [reportScope]
  );

  const tableUtrs = useMemo(
    () =>
      scopePacks.length === 0
        ? []
        : blueprintUtrs
            .filter(isOverridableShapeUtr)
            .filter((utr) => isMatchSelectedPacks({ utr, packs: scopePacks, metricGroups })),
    [blueprintUtrs, scopePacks, metricGroups]
  );


  const utrsForModule = useMemo(() => {
    const cache = new Map<string | undefined, UniversalTrackerBlueprintMin[]>();
    return (moduleId: string | undefined) => {
      const cached = cache.get(moduleId);
      if (cached) return cached;
      const next = moduleId
        ? tableUtrs.filter((utr) =>
            isMatchSelectedPacks({ utr, packs: [moduleId], metricGroups })
          )
        : tableUtrs;
      cache.set(moduleId, next);
      return next;
    };
  }, [tableUtrs, metricGroups]);

  const utrToOption = (utr: UniversalTrackerBlueprintMin): Option<string> => ({
    value: utr.code,
    // typeCode prefix disambiguates UTRs with near-identical names.
    label: utr.typeCode ? `${utr.typeCode} — ${utr.name}` : utr.name,
    searchString: `${utr.code} ${utr.name} ${utr.typeCode ?? ''}`,
    tooltip: utr.code,
  });

  const [entries, setEntries] = useState<OverrideEntry[]>(() => toEntries(form.normalisedOverrides));

  // Export groups extras by exact-string match on label (collectExtraLabels
  // in the API); the CreatableSelect below suggests these to avoid typos
  // splitting one logical column into two.
  const existingExtraLabels = useMemo(() => {
    const labels = new Set<string>();
    for (const e of entries) {
      for (const ex of e.extras) {
        if (ex.label.trim()) labels.add(ex.label);
      }
    }
    return labels;
  }, [entries]);

  // Track the last record we sent to the parent. If form.normalisedOverrides changes
  // by reference to something we did NOT commit (e.g. async hydration, parent reset),
  // re-derive entries from it.
  const lastCommittedRef = useRef<Record<string, NormalisedOverrideConfig> | undefined>(
    form.normalisedOverrides
  );

  useEffect(() => {
    if (form.normalisedOverrides !== lastCommittedRef.current) {
      lastCommittedRef.current = form.normalisedOverrides;
      setEntries(toEntries(form.normalisedOverrides));
    }
  }, [form.normalisedOverrides]);

  const commit = useCallback(
    (next: OverrideEntry[]) => {
      setEntries(next);
      const record = toRecord(next);
      const value = Object.keys(record).length > 0 ? record : undefined;
      lastCommittedRef.current = value;
      onChangeForm({ normalisedOverrides: value });
    },
    [onChangeForm]
  );

  const updateEntry = (id: string, patch: (e: OverrideEntry) => OverrideEntry) =>
    commit(entries.map((entry) => (entry.id === id ? patch(entry) : entry)));

  const updateExtra = (entryId: string, extraId: string, patch: Partial<ExtraEntry>) =>
    updateEntry(entryId, (entry) => ({
      ...entry,
      extras: entry.extras.map((extra) => (extra.id === extraId ? { ...extra, ...patch } : extra)),
    }));

  const handleAdd = () => commit([...entries, { id: makeId(), utrCode: '', extras: [] }]);
  const handleRemove = (id: string) => commit(entries.filter((entry) => entry.id !== id));

  const handleAddExtra = (entryId: string) =>
    updateEntry(entryId, (entry) => ({
      ...entry,
      extras: [...entry.extras, { id: makeId(), label: '', columnCode: '' }],
    }));

  const handleRemoveExtra = (entryId: string, extraId: string) =>
    updateEntry(entryId, (entry) => ({
      ...entry,
      extras: entry.extras.filter((extra) => extra.id !== extraId),
    }));

  // Picking a different UTR invalidates column codes from the previous UTR's columns —
  // clear them rather than carry dangling references that would silently fail at report-gen.
  const handleChangeUtr = (entryId: string, utrCode: string) =>
    updateEntry(entryId, (entry) =>
      entry.utrCode === utrCode
        ? entry
        : { ...entry, utrCode, activityColumn: undefined, extras: [] }
    );

  const handleChangeModule = (entryId: string, moduleId: string | undefined) =>
    updateEntry(entryId, (entry) => {
      if (!entry.utrCode) {
        return { ...entry, moduleId };
      }
      const stillVisible = utrsForModule(moduleId).some((u) => u.code === entry.utrCode);
      return stillVisible
        ? { ...entry, moduleId }
        : { ...entry, moduleId, utrCode: '', activityColumn: undefined, extras: [] };
    });

  const incompleteCount = countIncompleteRows(entries);
  const hasNoOverridableUtrs = !blueprintLoading && !blueprintError && tableUtrs.length === 0;
  const hasError = blueprintError || metricGroupsError;

  // UTRs the backend will drop unless the user picks an Activity column.
  // Scoped to metrics that this report's selected surveys actually expose
  // (see reportScope above), so the count tracks what the user will see in
  // the export, not what the Subsidiary's blueprint contains overall.
  const configuredUtrCodes = useMemo(
    () => new Set(entries.map((e) => e.utrCode).filter((code) => code.trim() !== '')),
    [entries]
  );
  const unconfiguredOverridableCount = blueprintLoading || hasError
    ? 0
    : tableUtrs.filter((utr) => !configuredUtrCodes.has(utr.code)).length;

  return (
    <FormGroup row className='mt-4'>
      <Label sm={2}>Per-metric overrides</Label>
      <Col sm={10}>
        <div className='text-ThemeTextLight small mb-2'>
          Only needed for metrics with two or more dropdown columns (e.g. material + destination).
          Pick which column becomes <em>Activity</em> and, if you want to keep a second dropdown
          visible, add it as an extra column. Rows from other metrics leave those cells blank.
        </div>
        {blueprintLoading ? (
          <div className='text-ThemeTextLight small'>Loading metrics…</div>
        ) : null}
        {hasError ? (
          <div className='text-ThemeTextDanger small mb-2' role='alert'>
            Failed to load metric data. Refresh the page or try again later.
          </div>
        ) : null}
        {hasNoOverridableUtrs ? (
          <div className='text-ThemeTextLight small mb-2'>
            No metrics in this {SURVEY.CAPITALIZED_SINGULAR} support normalised overrides.
            Overrides are only available for table metrics with at least one dropdown column and
            one numeric column.
          </div>
        ) : null}
        {incompleteCount > 0 ? (
          <div className='text-ThemeTextLight small mb-2'>
            <i className='fal fa-circle-info mr-1' />
            {incompleteCount === 1
              ? '1 incomplete row will be skipped on save.'
              : `${incompleteCount} incomplete rows will be skipped on save.`}
          </div>
        ) : null}
        {unconfiguredOverridableCount > 0 ? (
          <div className='text-ThemeTextWarning small mb-2' role='alert'>
            <i className='fal fa-triangle-exclamation mr-1' />
            {unconfiguredOverridableCount === 1
              ? `1 metric in this ${SURVEY.CAPITALIZED_SINGULAR} needs an override and will be dropped from the export until configured.`
              : `${unconfiguredOverridableCount} metrics in this ${SURVEY.CAPITALIZED_SINGULAR} need overrides and will be dropped from the export until configured.`}
          </div>
        ) : null}
        {entries.length === 0 ? (
          <div className='text-ThemeTextLight small'>No overrides configured.</div>
        ) : (
          entries.map((entry) => {
            const selectedUtr = entry.utrCode ? utrByCode.get(entry.utrCode) : undefined;
            const valueListColumnOptions = buildColumnOptions(selectedUtr, isValueListLike);
            const allColumnOptions = buildColumnOptions(selectedUtr);
            // Hide UTRs already configured on other rows so the user can't
            // accidentally configure two override rows for the same metric.
            // The current row's own selection stays visible so the dropdown
            // can render its label.
            const utrOptions = utrsForModule(entry.moduleId)
              .filter((utr) => utr.code === entry.utrCode || !configuredUtrCodes.has(utr.code))
              .map(utrToOption);
            const moduleOption = moduleOptions.find((o) => o.value === entry.moduleId) ?? null;
            const utrOption = utrOptions.find((o) => o.value === entry.utrCode) ?? null;
            const activityOption =
              valueListColumnOptions.find((o) => o.value === entry.activityColumn) ?? null;
            return (
              <div
                key={entry.id}
                className='border rounded p-3 mb-3 border-ThemeBorderDefault'
                data-testid={`normalised-override-${entry.id}`}
              >
                <Row>
                  <Col sm={4}>
                    <Label className='small'>Reporting module</Label>
                    <SelectFactory
                      selectType={SelectTypes.SingleSelect}
                      placeholder='All modules'
                      options={moduleOptions}
                      value={moduleOption}
                      onChange={(opt) => handleChangeModule(entry.id, opt?.value ?? undefined)}
                      isSearchable
                      isClearable
                      filterOption={SELECT_FILTER_OPTION}
                    />
                  </Col>
                  <Col sm={6}>
                    <Label className='small'>Metric</Label>
                    <SelectFactory
                      selectType={SelectTypes.SingleSelect}
                      placeholder='Select a metric…'
                      options={utrOptions}
                      value={utrOption}
                      onChange={(opt) => handleChangeUtr(entry.id, opt?.value ?? '')}
                      isSearchable
                      isClearable
                      filterOption={SELECT_FILTER_OPTION}
                      noOptionsMessage={() =>
                        tableUtrs.length === 0
                          ? 'No metrics support normalised overrides.'
                          : entry.moduleId
                            ? 'No metrics in this module — try clearing the module filter.'
                            : 'No matching metrics.'
                      }
                    />
                  </Col>
                  <Col sm={2} className='d-flex align-items-end justify-content-end'>
                    <Button
                      color='link-danger'
                      size='sm'
                      onClick={() => handleRemove(entry.id)}
                      aria-label={`Remove override ${entry.utrCode || entry.id}`}
                    >
                      <i className='fal fa-trash' />
                    </Button>
                  </Col>
                </Row>
                <Row className='mt-3'>
                  <Col sm={12}>
                    <Label className='small'>Activity column</Label>
                    <SelectFactory
                      selectType={SelectTypes.SingleSelect}
                      placeholder={selectedUtr ? 'Select a dropdown column…' : 'Pick a metric first'}
                      options={valueListColumnOptions}
                      value={activityOption}
                      onChange={(opt) =>
                        updateEntry(entry.id, (curr) => ({
                          ...curr,
                          activityColumn: opt?.value ?? undefined,
                        }))
                      }
                      isSearchable
                      isClearable
                      isDisabled={!selectedUtr}
                    />
                  </Col>
                </Row>
                <div className='mt-3'>
                  <Label className='small'>Extra columns (optional)</Label>
                  {entry.extras.length === 0 ? (
                    <div className='text-ThemeTextLight small mb-2'>No extras.</div>
                  ) : (
                    entry.extras.map((extra) => {
                      const extraColumnOption =
                        allColumnOptions.find((o) => o.value === extra.columnCode) ?? null;
                      // Suggest sibling labels; picking one shares the column.
                      const labelOptions: Option<string>[] = [...existingExtraLabels]
                        .filter((label) => label !== extra.label)
                        .map((label) => ({ value: label, label }));
                      const labelOption = extra.label
                        ? { value: extra.label, label: extra.label }
                        : null;
                      return (
                        <Row key={extra.id} className='mb-2 align-items-end'>
                          <Col sm={5}>
                            <Label className='small mb-1'>Column header</Label>
                            <SelectFactory
                              selectType={SelectTypes.CreatableSelect}
                              placeholder='e.g. End of Life Treatment'
                              options={labelOptions}
                              value={labelOption}
                              onChange={(opt) =>
                                updateExtra(entry.id, extra.id, { label: opt?.value ?? '' })
                              }
                              onCreateOption={(input) =>
                                updateExtra(entry.id, extra.id, { label: input })
                              }
                              formatCreateLabel={(input) => `Use "${input}" as a new column`}
                              isClearable
                            />
                          </Col>
                          <Col sm={5}>
                            <Label className='small mb-1'>Source column</Label>
                            <SelectFactory
                              selectType={SelectTypes.SingleSelect}
                              placeholder={
                                selectedUtr ? 'Select a column…' : 'Pick a metric first'
                              }
                              options={allColumnOptions}
                              value={extraColumnOption}
                              onChange={(opt) =>
                                updateExtra(entry.id, extra.id, {
                                  columnCode: opt?.value ?? '',
                                })
                              }
                              isSearchable
                              isClearable
                              isDisabled={!selectedUtr}
                            />
                          </Col>
                          <Col sm={2} className='d-flex justify-content-end'>
                            <Button
                              color='link-danger'
                              size='sm'
                              onClick={() => handleRemoveExtra(entry.id, extra.id)}
                              aria-label={`Remove extra ${extra.label || extra.id}`}
                            >
                              <i className='fal fa-trash' />
                            </Button>
                          </Col>
                        </Row>
                      );
                    })
                  )}
                  <Button
                    color='link-secondary'
                    size='sm'
                    onClick={() => handleAddExtra(entry.id)}
                    disabled={!selectedUtr}
                  >
                    <i className='fal fa-plus mr-1' />
                    Add extra column
                  </Button>
                </div>
              </div>
            );
          })
        )}
        <Button color='link-secondary' onClick={handleAdd} disabled={hasNoOverridableUtrs}>
          <i className='fal fa-plus mr-1' />
          Add metric override
        </Button>
      </Col>
    </FormGroup>
  );
};
